<template>
  <div>
    <v-dialog v-model="isOpen" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ data && data._id ? `Actualizar referencia: "${data.rfc}"` : 'Crear referencia' }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="referenceForm" v-model="form.valid">
            <v-container grid-list-xl>
              <v-layout wrap>
                <v-flex xs12 md12 v-if="!isEditing">
                  <v-autocomplete
                    v-model="searchRestaurant.selected"
                    :items="searchRestaurant.items"
                    :loading="searchRestaurant.isLoading"
                    :search-input.sync="searchRestaurant.search"
                    :rules="form.rules.provider"
                    hide-details
                    hide-no-data
                    hide-selected
                    item-text="name"
                    item-value="_id"
                    placeholder="Buscar restaurantes"
                  />
                </v-flex>
                <v-flex xs12 md12 v-if="isEditing">
                  <v-text-field
                    label="Restaurante"
                    v-model="form.data.restaurant"
                    disabled
                    required>
                  </v-text-field>
                </v-flex>
                <v-flex xs12 md12>
                  <v-text-field
                    v-model="form.data.reference"
                    :rules="form.rules.reference"
                    label="Referencia"
                    required>
                  </v-text-field>
                </v-flex>
                <v-flex xs12 md12>
                  <v-text-field
                    v-model="form.data.clabe"
                    :rules="form.rules.clabe"
                    label="Clabe o Cuenta Bancaria"
                    :append-icon="String(clabeCount)"
                    >
                  </v-text-field>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="closeDialog('close')">Cerrar</v-btn>
          <v-btn color="primary" :disabled="!form.valid" @click="submit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  props: {
    data: {
      type: Object
    }
  },
  data () {
    return {
      isOpen: false,
      overlay: false,
      searchProvider: {
        search: '',
        selected: null,
        items: [],
        isLoading: false
      },
      searchRestaurant: {
        search: '',
        selected: null,
        items: [],
        isLoading: false
      },
      form: {
        valid: false,
        rules: {
          provider: [v => !!v || `${this.$i18n.t('references.labels.provider')} ${this.$i18n.t('messages.isRequired')}`],
          rfc: [v => !!v || `${this.$i18n.t('references.labels.rfc')} ${this.$i18n.t('messages.isRequired')}`],
          reference: [v => !!v || `${this.$i18n.t('references.labels.reference')} ${this.$i18n.t('messages.isRequired')}`],
          clabe: [
            v => !!v || 'La clabe es requerida',
            v => /^\d+$/.test(v) || 'Sólo se permitén números',
            v => (v && v.length >= 10) || 'La clabe debe de tener por lo menos 10 carácteres',
            v => (v && v.length <= 18) || 'La clabe no puede exceder los 18 carácteres'
          ]
        },
        data: {
          id: null,
          provider: null,
          rfc: null,
          reference: null,
          clabe: null
        }
      },
      clabeCount: 0
    }
  },
  computed: {
    isEditing () {
      const isEditing = !!(this.form.data && this.form.data.id) || false
      return isEditing
    },
    provider () {
      return this.$store.state.currentProvider.data._id
    }
  },
  async mounted () {
    await this.getProvider()
  },
  watch: {
    isOpen: async function (val) {
      if (val && (this.data && this.data.rfc)) {
        this.form.data = {
          id: this.data._id || this.data.id,
          rfc: this.data.rfc,
          reference: this.data.reference,
          clabe: this.data.clabe,
          provider: this.provider,
          restaurant: this.data.restaurant
        }
      }
    },
    'searchRestaurant.search': debounce(async function (val) {
      if (val && val.length > 2) {
        this.searchRestaurant.isLoading = true
        const params = {
          name: `/${val}/i`,
          sort: 'name',
          limit: 20
        }
        try {
          const res = await this.$api.get(`/v1/restaurants`, params)
          this.searchRestaurant.items = res.data
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.searchRestaurant.isLoading = false
      }
    }, 300),
    'form.data.clabe': function (newVal) {
      if (!newVal || newVal === undefined) {
        newVal = 0
        this.clabeCount = newVal
      }
      this.clabeCount = newVal.length || 0
    }
  },
  methods: {
    async getProvider () {
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/providers/${this.provider}/settings`)
        this.form.data.provider = this.provider
        this.form.data.rfc = response.taxInformation.taxId
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    open () {
      this.isOpen = true
    },
    closeDialog (typeEvent) {
      this.isOpen = false
      this.resetForm()
      this.$emit(typeEvent)
    },
    async submit () {
      this.overlay = true
      if (this.$refs.referenceForm.validate()) {
        const request = this.form.data.id == null
          ? this.create()
          : this.update()
        try {
          await request
          this.closeDialog('refresh')
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3500, type: 'error', position: 'top-center', iconPack: 'mdi', icon: 'mdi-cancel' })
        }
      }
      this.overlay = false
    },
    async create () {
      this.form.data.restaurantId = this.searchRestaurant.selected
      await this.$api.post(`/v1/providers/${this.provider}/references`, this.form.data)
      this.$toasted.show(`Se creó exitosamente la referencia`, { duration: 3500, type: 'success', position: 'top-center', iconPack: 'mdi', icon: 'mdi-check-circle' })
    },
    async update () {
      this.form.data.restaurantId = this.data.restaurantId
      await this.$api.put(`/v1/providers/${this.provider}/references/${this.form.data.id}`, this.form.data)
      this.$toasted.show(`Se actualizó exitosamente la referencia`, { duration: 3500, type: 'success', position: 'top-center', iconPack: 'mdi', icon: 'mdi-check-circle' })
    },
    resetForm () {
      this.searchRestaurant = {
        search: '',
        selected: null,
        items: [],
        isLoading: false
      }
      this.clabeCount = 0
      this.form.data.id = null
      if (this.$refs && this.$refs.referenceForm) this.$refs.referenceForm.reset()
    }
  }
}
</script>
